import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './styles.module.css';
import Text from 'components/shared/Text';
import MobileContext from 'context/MobileContext';
import { Formik } from 'formik';
import { freezeFormSchema } from 'validation/auth.validation';
import Textfield from 'components/shared/Textfield';
import CustomDropdown from 'components/shared/SelectState';
import { Dialog, makeStyles } from '@material-ui/core';
import { checkmarkCircleOutline, infoIcon } from 'assets/newIcons';
import { CircularProgress } from '@mui/material';
import history from 'store/history';

const initialValues = {
  email: '',
  cancellationValue: '',
};

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: '12px',
  },
}));

const CancelMembership = ({
  loadCancelDropdownData,
  cancelDropdownDetails,
  submitCancelForm,
  isSubmitCancelLoading,
  cancelFormDetails,
  resetCancelFormDetails,
}) => {
  const isMobile = useContext(MobileContext);
  const userDetails = useSelector(state => state?.auth?.profile);
  const userFirstName = useSelector(state => state?.auth?.user?.firstName);
  const userPhoneNumber = userDetails?.phone || userDetails?.mobilephone;
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openThanksModal, setOpenThanksModal] = useState(false);
  const [onCancelForm, setOnCancelForm] = useState(false);
  const [cancellationValue, setCancellationValue] = useState('');
  const validData = cancellationValue;
  const classes = useStyles();
  const boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.08)';
  const inputWidth = isMobile ? '90.41vw' : '59.5vw';
  const cancellationOptions = cancelDropdownDetails?.dropDownData;
  const agreementText = cancelDropdownDetails?.agreementText;
  const cancelFormData = useSelector(state => state.membershipForms.formsDetails);
  const path = window.location.pathname;

  useEffect(() => {
    if (path === '/membership-form/cancel' && Object.keys(cancelFormData).length === 0) {
      history.push('/membership-form');
    }
  }, [cancelFormData, path]);

  useEffect(() => {
    if (Object.keys(cancelDropdownDetails).length === 0) {
      loadCancelDropdownData();
    }
  }, []);

  useEffect(() => {
    const isFormSubmittedFromStorage =
      localStorage.getItem('cancelFormSubmitted') === 'true';
    if ((onCancelForm || isFormSubmittedFromStorage) && cancelFormDetails?.success) {
      setOpenThanksModal(true);
    }
  }, [cancelFormDetails]);

  const handleCancellationDropdown = e => {
    setCancellationValue(e);
  };

  const handleInfoClick = () => {
    setOpenInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setOpenInfoModal(false);
  };

  const handleGoToBackClick = () => {
    setOpenThanksModal(false);
    setOnCancelForm(false);
    localStorage.removeItem('cancelFormSubmitted');
    resetCancelFormDetails();
    history.push('/');
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          const { email: optionalEmail, cancellationValue } = values;
          const { email, lastName } = userDetails;
          const data = {
            firstName: userFirstName,
            lastName,
            phoneNumber: userPhoneNumber,
            email: email,
            alternateEmail: optionalEmail,
            cancellationReason: cancellationValue,
            notes: 'dxfactor testing web',
            isPoral: true,
          };
          submitCancelForm(data);
          setOnCancelForm(true);
          localStorage.setItem('cancelFormSubmitted', 'true');
        }}
        validationSchema={freezeFormSchema}
      >
        {({ handleSubmit, handleChange, values, errors, touched }) => {
          return (
            <form
              className={`${styles.body} ${isMobile ? styles.mob : null}`}
              onSubmit={handleSubmit}
            >
              {!isMobile ? <Text className={styles.heading}>Cancel Membership</Text> : ''}
              {!userDetails ? (
                <div className={styles.spinnerContainer}>
                  <CircularProgress size={45} style={{ color: '#fe5000' }} />
                </div>
              ) : (
                <div className={styles.outerContainer}>
                  <div className={styles.innerContainer}>
                    <div className={styles.card}>
                      <div className={styles.details}>
                        <div className={styles.detailsText}>
                          <p className={styles.key}>Full Name</p>
                          <p className={styles.value}>{userDetails?.name}</p>
                        </div>
                        <div className={styles.detailsText} style={{ marginTop: '10px' }}>
                          <p className={styles.key}>Phone Number</p>
                          <p className={styles.value}>{userPhoneNumber}</p>
                        </div>
                        <div className={styles.detailsText} style={{ marginTop: '10px' }}>
                          <p className={styles.key}>Email</p>
                          <p className={styles.value}>{userDetails?.email}</p>
                        </div>
                      </div>
                      <div>
                        <img
                          src={infoIcon}
                          alt="icon"
                          className={styles.logo}
                          onClick={handleInfoClick}
                        />
                      </div>
                    </div>
                    <div className={styles.fieldContainer}>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label}>
                          If the email shown above is incorrect,{isMobile ? <br /> : ''}{' '}
                          please provide a new email{' '}
                          <span className={styles.optional}>(optional)</span>.
                        </p>
                        <Textfield
                          value={values.email}
                          onChange={handleChange}
                          name="email"
                          id="email"
                          placeholder="i.e. john@gmail.com"
                          width={inputWidth}
                          className={''}
                          invalid={touched.email && Boolean(errors.email)}
                          error={errors.email}
                          boxShadow={boxShadow}
                        />
                      </div>
                      <div
                        className={`${styles.inputWrapper} ${
                          isMobile ? styles.mobile : ''
                        }`}
                      >
                        <p className={styles.label}>Cancellation Reason*</p>
                        <CustomDropdown
                          height={'50px'}
                          backgroundColor={'white'}
                          borderRadius={'10px'}
                          marginTop={'5px'}
                          placeholder="Please Select"
                          name="cancellationValue"
                          id="cancellationValue"
                          className={''}
                          width={inputWidth}
                          hideIndicatorSeparator
                          options={cancellationOptions}
                          onChange={e => {
                            handleChange('cancellationValue')(e.value);
                            handleCancellationDropdown(e.label);
                          }}
                          value={values.cancellationValue}
                          boxShadow={boxShadow}
                        />
                      </div>
                      {agreementText && (
                        <div
                          className={styles.conditions}
                          dangerouslySetInnerHTML={{ __html: agreementText }}
                        />
                      )}
                    </div>
                  </div>
                  <div className={isMobile ? styles.buttonContainer : ''}>
                    <button
                      className={
                        !validData || isSubmitCancelLoading
                          ? styles.disableSubmitButton
                          : styles.submitButton
                      }
                      disabled={!validData || isSubmitCancelLoading}
                      type="submit"
                    >
                      {isSubmitCancelLoading ? (
                        <CircularProgress
                          size={20}
                          style={{ color: '#fe5000', marginTop: '5px' }}
                        />
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </div>
              )}
              <Dialog
                open={openInfoModal}
                onClose={handleCloseInfoModal}
                classes={{ paper: classes.dialogPaper }}
                disableScrollLock
              >
                <Text className={styles.infoText}>
                  If you would like to change this info, please speak to one of our
                  Customer Service reps.
                </Text>
              </Dialog>
              <Dialog open={openThanksModal} classes={{ paper: classes.dialogPaper }}>
                <div className={styles.iconWrapper}>
                  <img
                    alt=""
                    src={checkmarkCircleOutline}
                    className={`${styles.checkLogo} ${isMobile ? styles.mob : ''}`}
                  />
                  <Text className={styles.thanksText1}>Thank you!</Text>
                  <Text className={styles.thanksText2}>
                    A representative will be in touch soon.
                  </Text>
                  <p onClick={handleGoToBackClick} className={styles.goBackText}>
                    Go Back to Home
                  </p>
                </div>
              </Dialog>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default CancelMembership;
